import type { DestroyCookie, SetCookie } from '@packages/utilities';
import { useConfig, useCookies } from '@packages/utilities';
import { useEffect, useState } from 'react';

// import useCookieConsent from '../useCookieConsent';

const apiUrl =
  typeof window === 'undefined'
    ? 'https://lackmusapi.apigateway.empirie.<env>/lackmus/api'
    : '/lackmus/api';
const cookiePrefix = 'mvt_';

export type OutcomePayload = Record<string, string | number>;

export type TestRuleset =
  | {
      participant: string;
      variant: Record<string, string>;
    }
  | undefined;

export type Product = {
  sku: string;
  masterId?: string; // AKL
  count: number;
  value: number;
};

export type Payload = Record<string, string | number>;

export type Template = 'ATB' | 'RTH' | 'RPP';

export type APIPayload = {
  client: string;
  test: string;
  uuid: string;
  conversion: Array<string | number>;
  value: Array<string | number>;
  'User-Agent': string;
};

export type GetTestRulesetProps = (
  testId: string,
  client: string,
  cookies: {
    [x: string]: any;
  },
  setCookie?: SetCookie,
  removeCookie?: DestroyCookie,
  environment?: 'staging' | 'production',
) => Promise<TestRuleset>;

export const getTestRulesetFromAPI: GetTestRulesetProps = async (
  testId,
  client,
  cookies,
  setCookie,
  removeCookie,
  environment = 'production',
) => {
  const participantID =
    typeof cookies !== 'undefined' ? cookies[`${cookiePrefix}${testId}_participant`] : '';
  let testRuleset: TestRuleset;

  // build get url with testId and client
  let getUrl = `${apiUrl.replace(
    '<env>',
    environment === 'staging' ? 'dev' : 'cloud',
  )}/servlet/Participant?client=${encodeURIComponent(client)}&test=${testId}`;

  // if participant for the testId is in cookie, add it to the get url
  if (participantID) {
    getUrl = `${getUrl}&participant=${participantID}`;
  }

  const response = await fetch(getUrl, {
    headers: { 'Content-Type': 'application/json' },
  });
  // console.info('response:', response);

  const data = await response.json();

  // expiry date once for in a day and once in 30 days
  const date = new Date();
  const expiresOneDay = date.setUTCDate(date.getUTCDate() + 1);
  const expiresThirtyDay = date.setUTCDate(date.getUTCDate() + 30);

  if (typeof setCookie !== 'undefined') {
    if (data.participant) {
      // create test ruleset from data
      testRuleset = {
        participant: data.participant,
        variant: data.variant,
      };

      setCookie(null, cookiePrefix + testId, `${testRuleset}`, {
        expires: new Date(expiresOneDay),
        path: '/',
      });

      // set or reset participantID
      if (
        !cookies[`${cookiePrefix}${testId}_participant`] ||
        cookies[`${cookiePrefix}${testId}_participant`] !== data.participant
      ) {
        setCookie(null, `${cookiePrefix}${testId}_participant`, data.participant, {
          expires: new Date(expiresThirtyDay),
          path: '/',
        });
      }
    } else if (typeof removeCookie !== 'undefined') {
      removeCookie(null, `${cookiePrefix}${testId}_participant`, {
        expires: new Date(expiresThirtyDay),
        path: '/',
      });
    }
  }

  return testRuleset;
};

export const useAbTesting = () => {
  const { destroyCookie, getCookies, setCookie } = useCookies();
  const cookies = getCookies();
  const { tenantId: client } = useConfig();
  // const isConsent = useCookieConsent("C0002");

  /**
   * If you call this function for the first time in the customer journey a participantID for the user is generated and the test for the user is started.
   * The internal handling generates a API call if there is no data from the 'test_<testid>' cookie. Otherwise the data from the cookie is used to minimize API calls. The test-cookie expires after 24h.
   * This function is async. Please await the resultSet.
   * @param {string} testId the testId as communicated in the test manifest
   * @returns {TestRuleset} retuns the ruleset for the testId
   */
  const getTestRuleset = async (testId: string): Promise<TestRuleset> => {
    let currentRuleSet: TestRuleset;

    try {
      currentRuleSet = JSON.parse(cookies[cookiePrefix + testId]);
    } catch (e) {
      // ignore
    }

    // if (isConsent && !currentRuleSet) {
    if (!currentRuleSet) {
      currentRuleSet = await getTestRulesetFromAPI(
        testId,
        client,
        cookies,
        setCookie,
        destroyCookie,
      );
    }
    // }

    return currentRuleSet;
  };

  /**
   * Adds a `productId` to internal list of relevant products for the `testId`.
   * @param {string} testId the testId as communicated in the test manifest
   * @param {string} sku productId
   */
  const setRelevantProduct = (testId: string, sku: string | string[]) => {
    const relevantPrdouct = localStorage.getItem(testId);

    if (!relevantPrdouct) {
      localStorage.setItem(testId, JSON.stringify(typeof sku === 'string' ? [sku] : sku));
    } else {
      const products = JSON.parse(relevantPrdouct);
      // filter unique elements only
      const uniqueProducts = products
        .concat(sku)
        .filter((v: any, i: any, a: string | any[]) => a.indexOf(v) === i);
      localStorage.setItem(testId, JSON.stringify(uniqueProducts));
    }
  };

  /**
   * Returns a boolean for the `productId` if it was previously marked relevant for the `testId`.
   * @param {string} testId the testId as communicated in the test manifest
   * @param {string} sku productId
   * @returns {boolean} true if the sku is in the list of relevant products for the testId
   */
  const getRelevantProduct = (testId: string, sku: string): boolean => {
    const skuList: Array<string> = JSON.parse(localStorage.getItem(testId) || '[]');
    return skuList.includes(sku);
  };

  /**
   * Call the API and set a Outcome with the values from the payload. Internally adds a value for 'SESSIONID' from the value in the SESSIONID-Cookie.
   * @param {string} testId the testId as communicated in the test manifest
   * @param payload object of key/value pairs of type Payload
   * @returns void
   */
  const setOutcome = (testId: string, payload: Payload) => {
    const conv = ['SESSIONID'];
    const val = [cookies.SESSIONID];

    if (!cookies || !cookies[`${cookiePrefix}${testId}_participant`]) {
      return;
    }
    // generate payload for POST
    const apiPayload: APIPayload = {
      client,
      test: testId,
      uuid: cookies[`${cookiePrefix}${testId}_participant`],
      conversion: conv.concat(Object.keys(payload)),
      value: val.concat(Object.values(payload).join('')),
      'User-Agent': navigator.userAgent,
    };

    fetch(`${apiUrl}/servlet/Conversion`, {
      method: 'POST',
      body: JSON.stringify(apiPayload),
      headers: {
        'Content-type': 'application/json; charset=UTF-8',
      },
    });
  };

  /**
   * Tracks a outcome for the `productId` for all active tests. Checks if the productId was marked as relevant per test and adds the '_tr' prefix to the `template`if true.
   * @param {string} sku productId
   * @param {string} masterId masterId (AKL)
   * @param {Template} [template=RPP] the template from which the function is called
   */
  const setDefaultViewOutcome = (sku: string, masterId: string, template: Template = 'RPP') => {
    const tests = Object.keys(cookies).filter(
      (key) => key.startsWith(cookiePrefix) && key.indexOf('_participant') === -1,
    );

    tests.forEach((prefixedTestId) => {
      const testId = prefixedTestId.replace(cookiePrefix, '');
      // get relevant product status
      let isProductRelevant = getRelevantProduct(testId, sku);

      // if sku is relevant just add masterId to relevant products
      if (isProductRelevant) {
        setRelevantProduct(testId, masterId);
        // else check if AKL was previously marked as relevant and update isRelevantProduct
      } else {
        isProductRelevant = getRelevantProduct(testId, masterId);
      }

      const outcomePayload: OutcomePayload = {};
      outcomePayload[isProductRelevant ? `${template}_tr` : template] = '1';

      // call setTestOutcome for the test
      setOutcome(testId, outcomePayload);
    });
  };

  /**
   *
   * @param {Product[]} products Array of all products for the tracking. Testrelevant products are calculated with the internal list of relevant products.
   * @param {Template} template the template from which the function is called.
   * @param {string} [orderId] optional id of the current order.
   */
  const setDefaultProductOutcome = (
    products: Product[],
    template: Template,
    orderId: string | undefined = undefined,
  ) => {
    // find all tests in cookies
    const tests = Object.keys(cookies).filter(
      (key) => key.startsWith(cookiePrefix) && key.indexOf('_participant') === -1,
    );

    // generate RTH-Value and RTH-Count
    let rthValue = 0;
    let rthCount = 0;

    products.forEach((article) => {
      rthValue += article.value;
      rthCount += article.count;
    });

    tests.forEach((prefixedTestId) => {
      const testId = prefixedTestId.replace(cookiePrefix, '');
      // get list off all products for the test
      const skuList = JSON.parse(localStorage.getItem(testId) || '[]');

      // calculate RTH-Value_tr and RTH-Count_tr for the test
      let rthValueTr = 0;
      let rthCountTr = 0;

      products.forEach((article) => {
        if (
          skuList.indexOf(article.sku) !== -1 ||
          (article.masterId !== undefined && skuList.indexOf(article.masterId) !== -1)
        ) {
          // for AKL case when sku is not in relevant products. We just add it for all. List is unique.
          setRelevantProduct(testId, article.sku);
          rthValueTr += article.value;
          rthCountTr += article.count;
        }
      });

      // generate payload for the test
      const outcomePayload: OutcomePayload = {};
      outcomePayload[`${template}_VALUE`] = rthValue;
      outcomePayload[`${template}_COUNT`] = rthCount;
      outcomePayload[`${template}_VALUE_tr`] = rthValueTr;
      outcomePayload[`${template}_COUNT_tr`] = rthCountTr;
      if (orderId && orderId !== '') {
        outcomePayload.ORDERID = orderId;
      }

      // call setTestOutcome for the test
      setOutcome(testId, outcomePayload);
    });
  };

  const setDefaultCustomerOutcome = (customertype: string) => {
    const tests = Object.keys(cookies).filter(
      (key) => key.startsWith(cookiePrefix) && key.indexOf('_participant') === -1,
    );
    tests.forEach((prefixedTestId) => {
      const testId = prefixedTestId.replace(cookiePrefix, '');

      const outcomePayload = {
        CUSTOMERTYPE: customertype,
      };

      // call setTestOutcome for the test
      setOutcome(testId, outcomePayload);
    });
  };

  return {
    getTestRuleset,
    setRelevantProduct,
    getRelevantProduct,
    setOutcome,
    setDefaultProductOutcome,
    setDefaultViewOutcome,
    setDefaultCustomerOutcome,
  };
};

/**
 * useMousemovement hook
 *
 * Use to find out, if the mouse has been moved.
 * Excludes mouse movement, if the userAgent includes `www.empiriecom.com`.
 * @returns {boolean} Boolean used to find out, if mouse has been moved once on page
 */
export const useMousemovement = () => {
  const [mouseMoved, setMouseMoved] = useState(false);

  const move = () => {
    if (!mouseMoved) {
      setMouseMoved(true);
    }
  };

  useEffect(() => {
    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
      const { userAgent } = navigator;
      if (!userAgent.toLowerCase().includes('www.empiriecom.com')) {
        document.addEventListener('mousemove', move, { once: true });
        document.addEventListener('touchstart', move, { once: true });
      }
    }
    return () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('touchstart', move);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { mouseMoved };
};
